import type { ReactNode } from 'react';
import { ValidationIssueGroup } from '@insio/shared-types';
import { Button } from '../../../components/Button';
import { Icon } from '../../../components/icons/Icon';
import { useTranslation } from '../../../providers/UIProvider';
import type { GroupedRow } from './types';

export interface GroupedRowItemProps {
  group: GroupedRow;
  onScrollTo?: (target: string) => void;
}

/**
 * One clustered row inside a severity section. Issues sharing the same
 * `code` are merged into a single entry whose sub-field labels are joined,
 * so a single broken rule that touches multiple fields renders only once.
 */
export function GroupedRowItem({ group, onScrollTo }: GroupedRowItemProps) {
  const t = useTranslation('components.validationBanner');

  const byCode = new Map<string, { fields: string[]; message: string; action?: ReactNode }>();
  for (const issue of group.issues) {
    // AI findings share one code but carry distinct model-authored messages —
    // every one of them must render.
    const dedupKey = issue.groups?.includes(ValidationIssueGroup.AI_ANALYSIS)
      ? `${issue.code}|${issue.message}`
      : (issue.code ?? issue.message);
    const existing = byCode.get(dedupKey);
    if (existing) {
      if (issue.groupSubFieldLabel) existing.fields.push(issue.groupSubFieldLabel);
    } else {
      byCode.set(dedupKey, {
        fields: issue.groupSubFieldLabel ? [issue.groupSubFieldLabel] : [],
        message: issue.message,
        action: issue.action
      });
    }
  }

  const entries = Array.from(byCode.values());

  return (
    <div className='flex items-start gap-sm py-2xs group/row'>
      <div className='text-xs leading-snug'>
        <span className='font-semibold text-foreground-base'>{group.groupLabel}</span>
        {onScrollTo && (
          <Button
            variant='ghost'
            size='icon'
            onClick={() => onScrollTo(group.groupId)}
            className='ml-xs opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100 transition-opacity hover:bg-black/10 align-middle'
            aria-label={t('scrollTo')}
          >
            <Icon name='arrow-right-from-bracket' className='h-3 w-3 text-foreground-muted' />
          </Button>
        )}
        {entries.map((entry, i) => (
          <ul key={i}>
            <li className='font-medium text-foreground-base list-disc list-inside'>
              <span className='text-foreground-muted'>{entry.message}</span>
              {entry.action && <div className='mt-xs font-normal'>{entry.action}</div>}
            </li>
          </ul>
        ))}
      </div>
    </div>
  );
}
